import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotUsername from "@/pages/ForgotUsername";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import PortalLayout from "@/components/portal/PortalLayout";
import Dashboard from "@/pages/portal/Dashboard";
import Investments from "@/pages/portal/Investments";
import Plans from "@/pages/portal/Plans";
import Rewards from "@/pages/portal/Rewards";
import Team from "@/pages/portal/Team";
import Income from "@/pages/portal/Income";
import Deposit from "@/pages/portal/Deposit";
import Withdraw from "@/pages/portal/Withdraw";
import Wallet from "@/pages/portal/Wallet";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminDeposits from "@/pages/admin/AdminDeposits";
import AdminSweeps from "@/pages/admin/AdminSweeps";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminNews from "@/pages/admin/AdminNews";
import AdminRoyalty from "@/pages/admin/AdminRoyalty";
import AdminReports from "@/pages/admin/AdminReports";
import AdminIncomeReports from "@/pages/admin/AdminIncomeReports";

function Guard({ children, role }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen grid place-items-center bg-[#050507] text-amber-400 font-mono-fin">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/portal" replace />;
  if (role === "member" && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-right" theme="dark" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-username" element={<ForgotUsername />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/portal" element={<Guard role="member"><PortalLayout /></Guard>}>
              <Route index element={<Dashboard />} />
              <Route path="investments" element={<Investments />} />
              <Route path="plans" element={<Plans />} />
              <Route path="rewards" element={<Rewards />} />
              <Route path="team" element={<Team />} />
              <Route path="income" element={<Income />} />
              <Route path="deposit" element={<Deposit />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="withdraw" element={<Withdraw />} />
            </Route>

            <Route path="/admin" element={<Guard role="admin"><AdminLayout /></Guard>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="deposits" element={<AdminDeposits />} />
              <Route path="sweeps" element={<AdminSweeps />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="income-reports" element={<AdminIncomeReports />} />
              <Route path="royalty" element={<AdminRoyalty />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
